import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../store';
import { useEscapeToClose, useFocusTrap } from '../motion';
import { epicCardCount, epicsInUse, isEpicMerge } from '../epic-ops';
import EpicField from './EpicField';

/**
 * RENAME / REMOVE AN EPIC — the two cleanup dialogs opened from the Epics panel in
 * Settings (EpicSettings). An epic is free text on its cards, so both dialogs do the
 * one thing the store's reassignEpic does — move every card on this epic to another
 * name (or to none). The dialogs exist to make the reach honest: they lead with how
 * many cards change, rename offers a free merge when the name is already in use, and
 * remove makes the choice between keeping the cards on another epic or clearing them
 * an explicit decision rather than a bare confirm.
 *
 * Scope is this board (the panel's own cards) — the same per-board scope areas and
 * labels have. Shell and portal follow CorrectionDialog.
 *
 * ESCAPE is the shared useEscapeToClose (window, bubble phase), NOT a document-
 * capture listener, so the embedded move-target picker (EpicField) — which claims
 * Escape in the document CAPTURE phase and stops it — gets first refusal: one Escape
 * closes only the open picker, a second closes the dialog. A capture listener here
 * would fire before the picker's and tear the whole dialog down in one keystroke
 * (interaction + ux witnesses, 19 Oct; the L25 class). FOCUS is trapped with the
 * shared useFocusTrap so Tab stays inside the dialog and returns to the opener on
 * close, rather than walking the settings page behind the modal.
 */

const cardsWord = (n: number) => `${n} ${n === 1 ? 'card' : 'cards'}`;
// The cards referred back to, agreeing with the count — so a 1-card epic never
// reads "those cards … them" under a "1 card" headline (copy witness, 19 Oct).
const theseCards = (n: number) => (n === 1 ? 'that card' : 'those cards');
const themPron = (n: number) => (n === 1 ? 'it' : 'them');

export function RenameEpicDialog({ name, onClose }: { name: string; onClose: () => void }) {
  const issues = useApp((s) => s.issues);
  const reassignEpic = useApp((s) => s.reassignEpic);
  const [to, setTo] = useState(name);
  const input = useRef<HTMLInputElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  useEscapeToClose(onClose);
  useFocusTrap(panel);

  // Open with the name selected, so a quick retype replaces it.
  useEffect(() => {
    input.current?.focus();
    input.current?.select();
  }, []);

  const count = epicCardCount(issues, name);
  const trimmed = to.trim();
  const merge = isEpicMerge(issues, name, trimmed);
  const mergeInto = merge ? epicCardCount(issues, trimmed) : 0;
  const canSubmit = trimmed !== '' && trimmed !== name;

  const submit = () => {
    if (!canSubmit) return;
    reassignEpic(name, trimmed);
    onClose();
  };

  return createPortal(
    <div className="overlay" onClick={onClose}>
      <div ref={panel} tabIndex={-1} className="modal epic-dialog" role="dialog" aria-modal="true" aria-label="Rename epic" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><span className="modal-head-title">Rename epic</span></div>

        <div className="modal-body">
          <label className="epic-fld">
            <span className="af-l">Epic name<span className="req">*</span></span>
            <input
              ref={input}
              className="input"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
              aria-required
            />
          </label>

          {merge ? (
            <p className="epic-merge-note">
              An epic named <b>{trimmed}</b> already exists. Renaming will merge them: its{' '}
              {cardsWord(mergeInto)} and {count === 1 ? 'this' : 'these'} {cardsWord(count)} become
              one epic — <b>{cardsWord(mergeInto + count)}</b>.
            </p>
          ) : (
            <p className="hint">Renames this epic on <b>{cardsWord(count)}</b>, in one action.</p>
          )}
        </div>

        <div className="modal-actions">
          <button className="btn btn-small" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-small" disabled={!canSubmit} onClick={submit}>
            {merge ? 'Merge epics' : 'Rename'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function DeleteEpicDialog({ name, onClose }: { name: string; onClose: () => void }) {
  const issues = useApp((s) => s.issues);
  const reassignEpic = useApp((s) => s.reassignEpic);
  const panel = useRef<HTMLDivElement>(null);
  useEscapeToClose(onClose);
  useFocusTrap(panel);

  const count = epicCardCount(issues, name);
  const others = epicsInUse(issues).filter((e) => e !== name);
  const canMove = others.length > 0;
  // Default is Clear (the literal removal); Move is the opt-in for a merge/typo.
  const [mode, setMode] = useState<'clear' | 'move'>('clear');
  const [target, setTarget] = useState('');

  const movingReady = mode === 'move' && target.trim() !== '';
  const canSubmit = mode === 'clear' || movingReady;

  const submit = () => {
    if (!canSubmit) return;
    reassignEpic(name, mode === 'move' ? target.trim() : '');
    onClose();
  };

  return createPortal(
    <div className="overlay" onClick={onClose}>
      <div ref={panel} tabIndex={-1} className="modal epic-dialog" role="dialog" aria-modal="true" aria-label="Remove epic" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head"><span className="modal-head-title">Remove this epic?</span></div>

        <div className="modal-body">
          <p className="epic-del-lead">
            <b>{name}</b> is on <b>{cardsWord(count)}</b>. An epic is only a name on its cards, so
            removing it changes {theseCards(count)} — choose what happens to {themPron(count)}.
          </p>

          <div className="epic-del-opts" role="radiogroup" aria-label="What happens to the cards">
            <label className={`epic-del-opt ${mode === 'clear' ? 'is-on' : ''}`}>
              <input type="radio" name="epic-del" checked={mode === 'clear'} onChange={() => setMode('clear')} />
              <span className="epic-del-opt-text">
                <span className="epic-del-opt-name">Clear the epic off {themPron(count)}</span>
                <span className="epic-del-opt-sub">The {cardsWord(count)} {count === 1 ? 'becomes' : 'become'} “No epic”. The epic disappears from the list.</span>
              </span>
            </label>

            <label className={`epic-del-opt ${mode === 'move' ? 'is-on' : ''} ${canMove ? '' : 'is-disabled'}`}>
              <input
                type="radio"
                name="epic-del"
                checked={mode === 'move'}
                disabled={!canMove}
                onChange={() => setMode('move')}
              />
              <span className="epic-del-opt-text">
                <span className="epic-del-opt-name">Move {theseCards(count)} to another epic</span>
                <span className="epic-del-opt-sub">
                  {canMove
                    ? `${count === 1 ? 'It keeps' : 'They keep'} an epic — nothing is lost. Good for merging a duplicate or a typo.`
                    : `No other epic to move ${themPron(count)} to — clear ${themPron(count)} instead.`}
                </span>
                {mode === 'move' && canMove && (
                  <span className="epic-del-move">
                    <EpicField value={target} options={others} disabled={false} onChange={setTarget} allowNone={false} manageHint={false} />
                    {target.trim() !== '' && (
                      <span className="epic-del-moveto">{cardsWord(count)} {count === 1 ? 'moves' : 'move'} to <b>{target.trim()}</b>.</span>
                    )}
                  </span>
                )}
              </span>
            </label>
          </div>

          <p className="hint epic-del-foot">
            You can re-add the epic to any card afterwards — the cards stay, only their epic changes.
          </p>
        </div>

        <div className="modal-actions">
          <button className="btn btn-small" onClick={onClose}>Cancel</button>
          {mode === 'move' ? (
            <button className="btn btn-primary btn-small" disabled={!canSubmit} onClick={submit}>
              Move cards &amp; remove epic
            </button>
          ) : (
            <button className="btn btn-danger-solid btn-small" onClick={submit}>
              Clear &amp; remove epic
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
